/**
 * WebbEss E-commerce System - Modular Entry Point
 * Development version with ES6 modules
 * 
 * This file replaces the monolithic ecommerce-system.js with a modular approach.
 * All modules are loaded dynamically and the system is initialized automatically.
 */

// Import all core modules
import { getStorage, setStorage, clearStorage } from './core/storage.js';
import { escapeHtml, formatPrice, debounce, getElement, getAllElements, dispatchCartUpdatedEvent } from './core/utils.js';
import { ECommerceSystem } from './core/system.js';
import i18n, { setLanguage, setCurrency, t, getLanguage, getCurrencyCode, getCurrencyConfig } from './core/i18n.js';

// Import all feature modules
import { ShoppingCart } from './modules/cart.js';
import { ProductManager } from './modules/products.js';
import { FilterManager } from './modules/filters.js';
import { SearchDropdown } from './modules/search.js';
import { SearchPage } from './modules/search-page.js';
import { ProductVariantManager } from './modules/variants.js';
import { CategoryVariantManager } from './modules/category-variants.js';
import { CartHeaderButton } from './modules/cart-button.js';
import { InlineCart } from './modules/inline-cart.js';
import { GiftCardFormManager } from './modules/gift-card-form.js';
import { ShippingOptionsManager } from './modules/shipping-options.js';
import { DiscountManager } from './modules/discount.js';

// Import checkout modules
import { CheckoutManager } from './checkout/manager.js';
import { OrderConfirmationHandler } from './checkout/order-confirmation.js';

// Import payment providers
import { PaymentProvider } from './checkout/providers/base.js';
import { KlarnaProvider } from './checkout/providers/klarna.js';
import { BamboraProvider } from './checkout/providers/bambora.js';
import { StripeProvider } from './checkout/providers/stripe.js';
import { NoneProvider } from './checkout/providers/none.js';

// Export all modules for external access
export {
    // Core
    ECommerceSystem,
    getStorage,
    setStorage,
    clearStorage,
    escapeHtml,
    formatPrice,
    debounce,
    getElement,
    getAllElements,
    dispatchCartUpdatedEvent,
    
    // i18n
    i18n,
    setLanguage,
    setCurrency,
    t,
    getLanguage,
    getCurrencyCode,
    getCurrencyConfig,
    
    // Modules
    ShoppingCart,
    ProductManager,
    FilterManager,
    SearchDropdown,
    SearchPage,
    ProductVariantManager,
    CategoryVariantManager,
    CartHeaderButton,
    InlineCart,
    GiftCardFormManager,
    ShippingOptionsManager,
    DiscountManager,

    // Checkout
    CheckoutManager,
    OrderConfirmationHandler,
    
    // Payment Providers
    PaymentProvider,
    KlarnaProvider,
    BamboraProvider,
    StripeProvider,
    NoneProvider
};

// Auto-initialization logic
let ecommerceSystem = null;

/**
 * Initialize the e-commerce system with configuration
 */
function initializeECommerceSystem(config = {}) {
    if (ecommerceSystem) {
        console.warn('ECommerceSystem already initialized');
        return ecommerceSystem;
    }

    try {
        // Initialize i18n FIRST with language and currency from config
        if (config.language) {
            console.log('ECommerceSystem: Setting language to', config.language);
            setLanguage(config.language);
        }
        if (config.currency) {
            console.log('ECommerceSystem: Setting currency to', config.currency);
            setCurrency(config.currency);
        }
        
        // Create system instance with all modules
        ecommerceSystem = new ECommerceSystem(config);
        
        // Set up integration between modules
        setupModuleIntegration(ecommerceSystem);
        
        // Expose globally for backward compatibility
        window.ecommerce = ecommerceSystem;
        window.ECommerceSystem = ECommerceSystem;
        
        // Add CSS class to indicate system is loaded
        document.documentElement.classList.add('ecommerce-loaded');
        
        // Dispatch ready event
        const readyEvent = new CustomEvent('ecommerceReady', {
            detail: { instance: ecommerceSystem }
        });
        document.dispatchEvent(readyEvent);
        
        console.log('ECommerceSystem initialized successfully (modular version)');
        return ecommerceSystem;
        
    } catch (error) {
        console.error('Failed to initialize ECommerceSystem:', error);
        throw error;
    }
}

/**
 * Set up integration between modules
 */
function setupModuleIntegration(ecommerceSystem) {
    console.log('ECommerceSystem: Setting up module integration');
    
    // Säkra null-checks för modules-objektet
    if (!ecommerceSystem || !ecommerceSystem.modules) {
        console.warn('ECommerceSystem: modules object not available, skipping integration setup');
        return;
    }
    
    // Integration between ShippingOptionsManager and DiscountManager
    if (ecommerceSystem.modules.shippingOptions && ecommerceSystem.modules.discount) {
        console.log('ECommerceSystem: Setting up shipping-discount integration');
        
        // Lyssna på frakt-ändringar för att uppdatera automatiska rabatter
        document.addEventListener('shippingUpdated', (event) => {
            console.log('ECommerceSystem: Shipping updated, updating discount stack');
            if (ecommerceSystem.modules.discount && typeof ecommerceSystem.modules.discount.updateAutomaticDiscounts === 'function') {
                ecommerceSystem.modules.discount.updateAutomaticDiscounts();
            }
        });
        
        // Lyssna på cart-ändringar för att uppdatera automatiska rabatter
        document.addEventListener('cartUpdated', (event) => {
            console.log('ECommerceSystem: Cart updated, updating discount stack');
            if (ecommerceSystem.modules.discount && typeof ecommerceSystem.modules.discount.updateAutomaticDiscounts === 'function') {
                ecommerceSystem.modules.discount.updateAutomaticDiscounts();
            }
        });
    }
    
    // Integration between CheckoutManager and DiscountManager
    if (ecommerceSystem.modules.checkout && ecommerceSystem.modules.discount) {
        console.log('ECommerceSystem: Setting up checkout-discount integration');
        
        // Lyssna på checkout-ändringar för att uppdatera rabatter
        document.addEventListener('checkoutUpdated', (event) => {
            console.log('ECommerceSystem: Checkout updated, updating discount stack');
            if (ecommerceSystem.modules.discount && typeof ecommerceSystem.modules.discount.updateAutomaticDiscounts === 'function') {
                ecommerceSystem.modules.discount.updateAutomaticDiscounts();
            }
        });
    }
    
    // Central total calculation system
    console.log('ECommerceSystem: Setting up central total calculation system');
    document.addEventListener('requestTotalUpdate', (event) => {
        console.log('ECommerceSystem: Received requestTotalUpdate from:', event.detail.source);
        calculateAndUpdateTotals(ecommerceSystem, event.detail);
    });
    
    // Multi-discount event handling
    if (ecommerceSystem.modules.discount) {
        console.log('ECommerceSystem: Setting up multi-discount event handling');
        
        // Lyssna på när rabatt-stacken uppdateras
        document.addEventListener('discountStackUpdated', (event) => {
            console.log('ECommerceSystem: Discount stack updated', event.detail);
            
            // Uppdatera checkout-totaler om checkout finns
            if (ecommerceSystem.modules.checkout) {
                ecommerceSystem.modules.checkout.updateTotals();
            }
            
            // Uppdatera cart-display om cart finns
            if (ecommerceSystem.modules.cart && typeof ecommerceSystem.modules.cart.updateDisplay === 'function') {
                ecommerceSystem.modules.cart.updateDisplay();
            } else if (ecommerceSystem.modules.cartButton && typeof ecommerceSystem.modules.cartButton.refresh === 'function') {
                // Fallback: uppdatera cart-knappen istället
                ecommerceSystem.modules.cartButton.refresh();
            }
        });
        
        // Lyssna på manuella rabattkod-ändringar
        document.addEventListener('discountCodeEntered', (event) => {
            console.log('ECommerceSystem: Manual discount code entered', event.detail.code);
            ecommerceSystem.modules.discount.validateAndApplyManualDiscount(event.detail.code);
        });
        
        // Lyssna på när manuell rabatt tas bort
        document.addEventListener('discountCodeRemoved', (event) => {
            console.log('ECommerceSystem: Manual discount code removed');
            ecommerceSystem.modules.discount.removeManualDiscount();
        });
        
        // Lyssna på automatiska rabatter som appliceras
        document.addEventListener('automaticDiscountsApplied', (event) => {
            console.log('ECommerceSystem: Automatic discounts applied', event.detail);
            
            // Trigga uppdatering av rabatt-stacken
            const stackEvent = new CustomEvent('discountStackUpdated', {
                detail: {
                    automaticDiscounts: event.detail.discounts,
                    totalAutomaticDiscount: event.detail.totalAmount
                }
            });
            document.dispatchEvent(stackEvent);
        });
        
        // Lyssna på manuell rabatt som appliceras
        document.addEventListener('manualDiscountApplied', (event) => {
            console.log('ECommerceSystem: Manual discount applied', event.detail);
            
            // Trigga uppdatering av rabatt-stacken
            const stackEvent = new CustomEvent('discountStackUpdated', {
                detail: {
                    manualDiscount: event.detail.discount,
                    manualDiscountAmount: event.detail.amount
                }
            });
            document.dispatchEvent(stackEvent);
        });
    }
    
    console.log('ECommerceSystem: Module integration setup complete');
}

/**
 * Get configuration from various sources
 */
function getConfiguration() {
    let config = {};
    
    // 1. Check for global configuration object
    if (typeof window.ecommerceConfig !== 'undefined') {
        config = { ...config, ...window.ecommerceConfig };
    }
    
    // 2. Check for configuration in script tag data attributes
    const scriptTag = document.querySelector('script[src*="ecommerce-system.js"]');
    if (scriptTag) {
        const dataConfig = scriptTag.dataset.config;
        if (dataConfig) {
            try {
                const parsedConfig = JSON.parse(dataConfig);
                config = { ...config, ...parsedConfig };
            } catch (error) {
                console.warn('Failed to parse script tag configuration:', error);
            }
        }
    }
    
    // 3. Check for configuration in meta tags
    const configMeta = document.querySelector('meta[name="ecommerce-config"]');
    if (configMeta) {
        try {
            const metaConfig = JSON.parse(configMeta.getAttribute('content'));
            config = { ...config, ...metaConfig };
        } catch (error) {
            console.warn('Failed to parse meta tag configuration:', error);
        }
    }
    
    return config;
}

/**
 * Auto-initialization when DOM is ready
 */
function autoInitialize() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            const config = getConfiguration();
            initializeECommerceSystem(config);
        });
    } else {
        // DOM is already ready
        const config = getConfiguration();
        initializeECommerceSystem(config);
    }
}

// Start auto-initialization
autoInitialize();

// Export initialization function for manual use
export { initializeECommerceSystem };

// Backward compatibility: expose on window
window.initializeECommerceSystem = initializeECommerceSystem;

/**
 * Central total calculation function
 * Handles the correct order: subtotal + shipping - discounts = total
 * B2B-aware: adds VAT when pricesIncludeVat = false
 */
function calculateAndUpdateTotals(ecommerceSystem, eventDetail) {
    console.log('ECommerceSystem: Calculating and updating totals', eventDetail);
    
    // Prevent recursive calls
    if (ecommerceSystem.isCalculatingTotals) {
        console.log('ECommerceSystem: Preventing recursive total calculation');
        return;
    }
    
    ecommerceSystem.isCalculatingTotals = true;
    
    try {
        // Get DOM elements
        const subtotalElement = document.getElementById('order-subtotal-amount');
        const shippingElement = document.getElementById('order-shipping-amount');
        const totalElement = document.getElementById('order-total-amount');
        const vatElement = document.getElementById('order-vat-amount');
        
        if (!subtotalElement || !totalElement) {
            console.warn('ECommerceSystem: Required total elements not found');
            return;
        }
        
        // Check if B2B mode (prices exclude VAT)
        const pricesIncludeVat = ecommerceSystem?.config?.pricesIncludeVat ?? true;
        console.log('ECommerceSystem: pricesIncludeVat =', pricesIncludeVat);
        
        // Parse current subtotal
        const subtotal = parsePrice(subtotalElement.textContent);
        console.log('ECommerceSystem: Subtotal:', subtotal);
        
        // Get shipping cost
        let shippingCost = 0;
        if (eventDetail.source === 'shipping' && eventDetail.shippingCost !== undefined) {
            shippingCost = eventDetail.shippingCost;
        } else if (ecommerceSystem.modules.shippingOptions) {
            shippingCost = ecommerceSystem.modules.shippingOptions.getShippingCost() || 0;
        } else if (shippingElement) {
            shippingCost = parsePrice(shippingElement.textContent);
        }
        console.log('ECommerceSystem: Shipping cost:', shippingCost);
        
        // Get discount amount
        let discountAmount = 0;
        if (eventDetail.source === 'discount' && eventDetail.totalDiscountAmount !== undefined) {
            discountAmount = eventDetail.totalDiscountAmount;
        } else if (ecommerceSystem.modules.discount) {
            discountAmount = ecommerceSystem.modules.discount.getTotalDiscountAmount() || 0;
        }
        console.log('ECommerceSystem: Discount amount:', discountAmount);
        
        // Calculate final total based on B2C/B2B mode
        let finalTotal;
        let totalVat = 0;
        
        if (pricesIncludeVat) {
            // B2C: Simple calculation - prices already include VAT
            finalTotal = Math.max(0, subtotal + shippingCost - discountAmount);
            console.log('ECommerceSystem: B2C Final total calculation:', {
                subtotal,
                shippingCost,
                discountAmount,
                finalTotal
            });
        } else {
            // B2B: Need to add VAT to total
            // IMPORTANT: In B2B, discounts are applied BEFORE VAT calculation
            // Formula: ((subtotal + shipping - discount) * (1 + vat_rate))
            
            // Get VAT rate from cart items (assume 25% as default)
            const cartItems = getStorage(ecommerceSystem?.config?.storageKey || 'cartItems');
            const productTaxRate = cartItems[0]?.TaxRate || 25;
            
            // Get shipping tax rate
            let shippingTaxRate = 25;
            if (ecommerceSystem.modules.shippingOptions) {
                const shippingData = ecommerceSystem.modules.shippingOptions.getShippingData();
                if (shippingData && shippingData.shippingTaxRate) {
                    shippingTaxRate = shippingData.shippingTaxRate;
                }
            }
            
            // Calculate subtotal excl VAT after discount
            // Assume discount applies proportionally to products and shipping
            const subtotalBeforeDiscount = subtotal + shippingCost;
            const subtotalAfterDiscount = Math.max(0, subtotalBeforeDiscount - discountAmount);
            
            // Calculate how discount is distributed between products and shipping
            let productAmountAfterDiscount = subtotal;
            let shippingAmountAfterDiscount = shippingCost;
            
            if (discountAmount > 0 && subtotalBeforeDiscount > 0) {
                // Distribute discount proportionally
                const discountRatio = discountAmount / subtotalBeforeDiscount;
                productAmountAfterDiscount = subtotal * (1 - discountRatio);
                shippingAmountAfterDiscount = shippingCost * (1 - discountRatio);
            }
            
            // Calculate VAT on discounted amounts
            const productVat = productAmountAfterDiscount * (productTaxRate / 100);
            const shippingVat = shippingAmountAfterDiscount * (shippingTaxRate / 100);
            totalVat = productVat + shippingVat;
            
            // Total = subtotal after discount + VAT
            finalTotal = subtotalAfterDiscount + totalVat;
            
            console.log('ECommerceSystem: B2B Final total calculation:', {
                subtotal,
                shippingCost,
                discountAmount,
                subtotalBeforeDiscount,
                subtotalAfterDiscount,
                productAmountAfterDiscount,
                shippingAmountAfterDiscount,
                productVat,
                shippingVat,
                totalVat,
                finalTotal
            });
            
            // Update VAT display if element exists
            if (vatElement) {
                vatElement.textContent = formatPrice(totalVat);
            }
        }

        // Gift card payment (Phase 3): deducted LAST - after VAT and discounts
        // are fully computed (B10). The amount never affects VAT or the
        // discount base. Without an applied card this is always 0 and the
        // calculation is identical to before.
        const giftCardPaymentManager = window.ecommerce?.checkout?.giftCardPaymentManager;
        const giftCardAmount = giftCardPaymentManager ? giftCardPaymentManager.recalculate(finalTotal) : 0;
        if (giftCardAmount > 0) {
            finalTotal = Math.max(0, finalTotal - giftCardAmount);
        }

        // Update total in DOM
        totalElement.textContent = formatPrice(finalTotal);
        
        // Dispatch event to notify other modules
        const event = new CustomEvent('totalsUpdated', {
            detail: {
                subtotal,
                shippingCost,
                discountAmount,
                totalVat,
                finalTotal,
                pricesIncludeVat
            }
        });
        document.dispatchEvent(event);
        
    } catch (error) {
        console.error('ECommerceSystem: Error calculating totals:', error);
    } finally {
        ecommerceSystem.isCalculatingTotals = false;
    }
}

/**
 * Parse price text to number
 */
function parsePrice(priceText) {
    if (!priceText) return 0;
    // Parse "1 234,56 kr" or "1234.56 kr" format
    const cleanText = priceText.replace(/[^\d,.-]/g, '');
    const normalizedText = cleanText.replace(',', '.');
    return parseFloat(normalizedText) || 0;
}

// For debugging
if (typeof window !== 'undefined') {
    window.ecommerceModules = {
        ECommerceSystem,
        ShoppingCart,
        ProductManager,
        FilterManager,
        SearchDropdown,
        SearchPage,
        ProductVariantManager,
        CategoryVariantManager,
        CartHeaderButton,
        InlineCart,
        GiftCardFormManager,
        ShippingOptionsManager,
        DiscountManager,
        CheckoutManager,
        OrderConfirmationHandler,
        PaymentProvider,
        KlarnaProvider,
        BamboraProvider,
        StripeProvider,
        NoneProvider
    };
}
